import { ClickAnalyticsPlugin } from '@microsoft/applicationinsights-clickanalytics-js';
import { AppInsightsContext, ReactPlugin } from '@microsoft/applicationinsights-react-js';
import type { ITelemetryItem } from '@microsoft/applicationinsights-web';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { useMemo } from 'react';

import { useAuth } from '@/context/auth';
import { setCrashReportingClient } from '@/services/crashReporting';

export const AppInsightsContextProvider: FCC = ({ children }) => {
  const { user } = useAuth();
  const reactPlugin = useMemo(() => {
    const reactPluginInstance = new ReactPlugin();
    const clickPluginInstance = new ClickAnalyticsPlugin();

    // Note: we cannot enable autoCapture because for in order for this to work we'd need to modify
    // all the links, buttons, etc. to have the necessary data attributes for the plugin to work.
    // But we can still track events manually where it's needed, see the documentation for more info:
    // https://learn.microsoft.com/en-us/azure/azure-monitor/app/javascript-framework-extensions?tabs=react#use-application-insights-with-react-context
    // Check the "EstimationButtonsContainer" for an example of how to track events manually.
    const clickPluginConfig = { autoCapture: false };

    const appInsightsConnectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING;
    const samplingRate = import.meta.env.VITE_APPINSIGHTS_SAMPLING_RATE;
    if (appInsightsConnectionString) {
      const appInsights = new ApplicationInsights({
        config: {
          samplingPercentage:
            samplingRate < 0 ? 0 : samplingRate > 100 ? 100 : Number(samplingRate),
          connectionString: appInsightsConnectionString,
          disableTelemetry: import.meta.env.DEV, // do NOT send telemetry from local development environment
          enableDebug: import.meta.env.DEV,
          extensions: [reactPluginInstance, clickPluginInstance],
          extensionConfig: {
            [clickPluginInstance.identifier]: clickPluginConfig,
          },
          enableAutoRouteTracking: true,
          disableAjaxTracking: false,
          autoTrackPageVisitTime: true,
          enableCorsCorrelation: true,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
        },
      });
      appInsights.loadAppInsights();
      // EWM-954: give the crash reporter a client so a white screen becomes an AppExceptions row,
      // which is where the team's alert rules and KQL runbooks look.
      setCrashReportingClient(appInsights);
      appInsights.addTelemetryInitializer((env: ITelemetryItem) => {
        env.tags = env.tags || {};
        env.tags['ai.cloud.role'] = 'estimator-frontend';
        env.data = env.data || {};
        env.data['ms-appName'] = 'estimator-frontend';
        env.data['ms-user'] = user?.email ?? 'anonymous';
        env.data['ms-userid'] = user?.id ?? 'anonymous';
      });
    }

    return reactPluginInstance;
  }, [user]);

  return <AppInsightsContext.Provider value={reactPlugin}>{children}</AppInsightsContext.Provider>;
};
